import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, signal } from '@angular/core';
import { TPaginationInfo } from '@monorepo/types';
import { TUserPaginationOption } from '../listing-pagination/pagination.types';
import { TSortingParameter } from '../listing-sorter/sorter.types';

const DEFAULT_PAGINATION_OPTION: TUserPaginationOption = { pageNo: 1, pageSize: 10 };
const MAX_VISIBLE_PAGES = 4;

const EMPTY_PAGINATION_INFO: TPaginationInfo = {
  totalPageCount: 0,
  totalItems: 0,
  rangeInPage: { start: 0, end: 0 },
};

@Component({
  selector: 'monorepo-pagination',
  templateUrl: './pagination.component.html',
  styleUrls: ['./pagination.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaginationComponent {
  @Input('paginationInfo') set paginationInfoInput(value: TPaginationInfo) {
    this.paginationInfo.set(value);

    const { rangeInPage, itemsPerPage = DEFAULT_PAGINATION_OPTION.pageSize } = value;
    const page = rangeInPage.start === 0 ? 1 : Math.ceil(rangeInPage.start / itemsPerPage);

    this.currentPage.set(page);
    this.visiblePages.set(this.#buildVisiblePages(page));
  }

  readonly paginationInfo = signal<TPaginationInfo>(EMPTY_PAGINATION_INFO);

  @Input() set paginationOption(v: TUserPaginationOption) {
    if (v && (v.pageNo !== this.#userPaginationOption.pageNo || v.pageSize !== this.#userPaginationOption.pageSize)) {
      this.#userPaginationOption = v;
      this.currentPageSize.set(v.pageSize);
    }
  }

  @Input() itemLabel = 'items';
  @Input() itemsPerPageList: Readonly<number[]> = [10, 20, 30, 40, 50];
  @Input() sortingInfo: TSortingParameter[] = [];
  @Input() selectedSortingOption: TSortingParameter | undefined;

  @Output() onPaginationUpdate = new EventEmitter<TUserPaginationOption>();
  @Output() onUserSortingUpdate = new EventEmitter<TSortingParameter>();

  #userPaginationOption: TUserPaginationOption = DEFAULT_PAGINATION_OPTION;
  readonly currentPage = signal(1);
  readonly currentPageSize = signal(DEFAULT_PAGINATION_OPTION.pageSize);
  readonly visiblePages = signal<number[]>([]);

  #changeCurrentPage(pageNo: number): void {
    if (pageNo === this.#userPaginationOption.pageNo) return;
    this.#userPaginationOption = { ...this.#userPaginationOption, pageNo };
    this.onPaginationUpdate.emit(this.#userPaginationOption);
  }

  goTo(page: number): void {
    if (page < 1 || page > this.paginationInfo().totalPageCount || page === this.currentPage()) return;
    this.#changeCurrentPage(page);
  }

  onPageSizeChange(pageSize: number): void {
    if (pageSize === this.#userPaginationOption.pageSize) return;
    this.#userPaginationOption = { pageNo: 1, pageSize };
    this.currentPageSize.set(pageSize);
    this.onPaginationUpdate.emit(this.#userPaginationOption);
  }

  onSortingOptionChange($event: Event): void {
    const updated: TSortingParameter = ($event as CustomEvent).detail.value;
    if (
      updated?.sortingKey !== this.selectedSortingOption?.sortingKey ||
      updated?.sortingOrder !== this.selectedSortingOption?.sortingOrder
    ) {
      this.selectedSortingOption = updated;
      this.onUserSortingUpdate.emit(this.selectedSortingOption);
    }
  }

  onSortingRemove(): void {
    this.selectedSortingOption = undefined;
    this.onUserSortingUpdate.emit(this.selectedSortingOption);
  }

  compareSortOptions(a: TSortingParameter, b: TSortingParameter): boolean {
    return a?.sortingKey === b?.sortingKey && a?.sortingOrder === b?.sortingOrder;
  }

  #buildVisiblePages(current: number): number[] {
    const total = this.paginationInfo().totalPageCount;

    if (total <= MAX_VISIBLE_PAGES) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    let start = Math.max(1, current - 1);
    const end = Math.min(total, start + MAX_VISIBLE_PAGES - 1);
    start = Math.max(1, end - MAX_VISIBLE_PAGES + 1);

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }
}
